import React, { useEffect, useRef } from 'react';
import useStore from '../../../../store';
import PuzzlePiece from './PuzzlePiece';
import './StartAnimation.css';

// Длительность анимации (должна совпадать с CSS)
export const ANIMATION_DURATION = 1500; // ms // Возвращаем стандартную длительность или можно увеличить

const StartAnimation = () => {
  useStore(state => state.puzzleRedraw); // Подписка на redraw для boardTransform

  const animationTimeoutRef = useRef(null);

  const { ref } = useStore.getState();
  const { puzzle } = ref;
  const { state, config, boardCanvas, methods: puzzleMethods, startAnimation, layout, boardTransform } = puzzle || {}; // <<< Достаем boardTransform
  const { pieces, imageScale } = state || {};
  const { trayPiecesLayout, trayContainer } = layout || {};
  const boardRect = boardCanvas?.rect;

  useEffect(() => {
    console.log('[StartAnimation] Component mounted, starting animation timeout...');
    animationTimeoutRef.current = setTimeout(() => {
      console.log('[StartAnimation] Animation finished. Syncing.');
      if (animationTimeoutRef.current !== null) {
        puzzle.config.showTray = Date.now();
        startAnimation.finished = Date.now();
        puzzleMethods.redraw();
        animationTimeoutRef.current = null;
      }
    }, ANIMATION_DURATION);

    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
        console.log('[StartAnimation] Animation timeout cleared on unmount.');
      }
    };
  }, [puzzleMethods, startAnimation, puzzle.config]); // Добавим зависимости

  if (!pieces || !config || !imageScale || !trayPiecesLayout || !boardRect || !trayContainer || !boardTransform || !state.pieceBaseDimensions) {
    console.warn('[StartAnimation] Missing data for rendering.');
    return null;
  }

  const { pieceLayoutData } = trayPiecesLayout;


  // --- Расчет центра доски с компенсацией ---
  const boardCenterX = boardRect.left + boardRect.width / 2;
  const boardCenterY = boardRect.top + boardRect.height / 2;

  return (
    <>
      {Object.keys(pieces).map(pieceId => {
        const piece = pieces[pieceId];
        const pieceLayout = pieceLayoutData[pieceId];

        // Нужны baseDimensions для расчета смещений
        if (!pieceLayout || !piece?.baseDimensions || !piece.correctPos) {
          return null;
        }

        const { scaledDimensions, scaledTotalDimensions, scaledTabExtensions, trayPosition } = puzzle.methods.calculateOnePiece(pieceId);

        // --- Расчет стартовой позиции (на доске) ---
        const basePosX_on_board = piece.correctPos.x * boardRect.width;
        const basePosY_on_board = piece.correctPos.y * boardRect.height;

        // Координаты top-left кусочка на доске (относительно viewport)
        const startX_piece_origin = boardRect.left + basePosX_on_board;
        const startY_piece_origin = boardRect.top + basePosY_on_board; 

        // Центр кусочка на доске (с компенсацией Y)
        const startCenterX = startX_piece_origin + (scaledDimensions.width / 2);
        const startCenterY = startY_piece_origin + (scaledDimensions.height / 2); // Y уже скомпенсирован

        // Вычисляем top-left для контейнера для начальной точки
        const startX_container = startX_piece_origin - scaledTabExtensions.width;
        const startY_container = startY_piece_origin - scaledTabExtensions.height; // Y уже скомпенсирован

        // --- Расчет промежуточной точки (разлет от центра доски) ---
        // Вектор от центра доски до *центра* кусочка (все Y скомпенсированы)
        const vectorX = startCenterX - boardCenterX;
        const vectorY = startCenterY - boardCenterY;

        // Увеличиваем вектор на 10% (коэффициент 1.1)
        const midCenterX = boardCenterX + vectorX * 1.1;
        const midCenterY = boardCenterY + vectorY * 1.1;

        // Преобразуем промежуточный *центр* в координаты top-left для контейнера
        const midX = midCenterX - (scaledDimensions.width / 2) - scaledTabExtensions.width;

        const midY = midCenterY - (scaledDimensions.height / 2) - scaledTabExtensions.height; // Y центра уже скомпенсирован

        // --- Правильный расчет для конечной точки ---
        // 1. Рассчитываем размеры и выступы ТОЛЬКО с imageScale (как в трее)

        // 2. Считаем конечные координаты viewport
        const endX = trayPosition.x - scaledTabExtensions.width;
        const endY = trayPosition.y - scaledTabExtensions.height;

        const initialRotation = Math.round(piece.rotation / 360) * 360;

        // Стили с CSS переменными (используем top-left контейнера)
        const style = {
          width: scaledTotalDimensions.width,
          height: scaledTotalDimensions.height,
          '--start-x': `${startX_container}px`,
          '--start-y': `${startY_container}px`,
          '--start-rotation': `${initialRotation}deg`,
          '--mid-x': `${midX}px`,
          '--mid-y': `${midY}px`,
          '--mid-rotation': `${initialRotation}deg`,
          '--end-x': `${endX}px`,
          '--end-y': `${endY}px`,
          '--end-rotation': `${piece.rotation}deg`,
          '--animation-duration': `${ANIMATION_DURATION}ms`,
        };

        return (
          <div
            key={pieceId}
            className="animated-piece-container"
            style={style}
          >
            <PuzzlePiece
                pieceId={pieceId}
                overrideStyle={{ transform: 'none' }}
                scaleFactor={imageScale}
            />
          </div>
        );
      })}
    </>
  );
};

export default StartAnimation; // Убираем React.memo, т.к. зависит от многих внешних данных
